// General utility functions

import {Dictionary} from "vue-router/types/router";

export class MsgException extends Error {
    private _msg: string;
    private _causedBy: MsgException | null = null;

    constructor(msg: string, causedBy?: MsgException) {
        super();
        this._msg = msg;
        this._causedBy = causedBy || null;
    }

    get msg(): string {
        var msg = this._msg;
        if(this._causedBy != null) {
            msg = msg + "\n\nCaused by:\n"+this._causedBy.msg;
        }
        return msg;
    }

    get message() {
        return this.msg;
    }
}

export class PropertyNotFoundException extends MsgException {
    constructor() {
        super("Tried to get value from undefined reference. The property was not found");
    }
}

export class WrongTypeException extends MsgException {
    private wantedType: string;
    private givenType: string;

    constructor(wantedType: string, reference: any) {
        super("Tried to get valued of type "+wantedType+", but reference given was type "+reference);
        this.wantedType = wantedType;
        this.givenType = typeof reference;
    }
}

export class JsonParseException extends MsgException {

    constructor(wantedType: string, causedBy?: MsgException) {
        super("JsonParseException: attempt to parse json to "+wantedType+" failed", causedBy);
    }

}

export class IllegalArgumentException extends MsgException {
    private _illegalArgument: any;

    constructor(illegalArgument: any, causedBy?: MsgException) {
        super("IllegalArgumentException: This argument is illegal: "+illegalArgument, causedBy);
        this._illegalArgument = illegalArgument;
    }

    get illegalArgument(): any {
        return this._illegalArgument;
    }
}


/** Try to get number from given reference, throw exception if not possible */
export function requireNum(something: any): number {
    var maybe: number = maybeNum(something);
    if(isNaN(maybe)) {
        if(typeof something == "undefined") {
            throw new PropertyNotFoundException();
        } else {
            throw new WrongTypeException("number", something);
        }
    }
    return maybe;
}

/** Try to get number from given reference. Return NaN if not possible */
export function maybeNum(something: any, strict: boolean = false): number {
    if(strict) {
        return something *1;
    } else {
        return parseFloat(something);
    }
}

export function numOrNull(something: any, strict: boolean = false): number | null {
    let ret = maybeNum(something, strict);
    if(ret != ret) { // NaN check
        return null;
    }
    return ret;
}

/** Try to get string from given reference. Return null if not possible */
export function maybeStr(something: any): string {
    let typ = typeof something;
    return typ == "undefined" || typ == "function" || typ == "object" ? null : something.toString();
}

/** Try to get string from given reference. Throw exception if not possible */
export function requireStr(something: any): string {
    if(typeof something == "undefined") {
        throw new PropertyNotFoundException();
    }
    let maybe = maybeStr(something);
    if(maybe == null) {
        throw new WrongTypeException("string", something);
    }
    return maybe;
}

export function numbersFromSeparatorListed(listed: any, separator: string = ","): number[] {
    // Return empty array on undefined or null input.
    if(!listed) {
        return [];
    }
    // Check if input is a single number already
    const n = numOrNull(listed, true);
    if(n != null) {
        return [n];
    }
    // Then see if it is a separated string we can parse
    if(typeof listed == "string") {
        return listed.split(separator).map(r => numOrNull(r, true)).filter(n => n != null) as number[]; // Safe to cast to number since we've filtered away any nulls.
    }
    // Throw error if invalid input
    throw new Error(`Invalid input to numbersFromSeparatorListed (separator ${separator}): ${listed}`);
}

/** Applies given function to given reference and return the result if reference is not null. If reference is null, return null */
export function noNullApply(reference: any, func: Function) {
    if(reference != null) {
        return func(reference);
    }
    return null;
}

/** Return string if it's not empty, null if the variable is null or empty */
export function nonEmptyString(str: string | null): string | null {
    if(str != null && str.length > 0) {
        return str;
    } else {
        return null;
    }
}

/** Return val if its not null, ifnull otherwise */
export function nvl(val: any, ifnull: any) {
    if(val != null && typeof val != "undefined") return val;
    else return ifnull;
}


/** Check if all elements of two given arrays are equal */
export function arrEquality(arr1: any[], arr2: any[]): boolean {
    if(arr1.length != arr2.length) {
        return false;
    }
    return arr1.every((v,i) => { return v == arr2[i]; });
}


export function nonemptyArray(arr: any[]): boolean {
    return arr != null && arr.length > 0;
}

export function firstOne<T>(inp: T | T[]): T | null {
    if(inp instanceof Array) {
        return inp[0] || null;
    } else {
        return inp || null;
    }
}

// In the spirit of Debouncer, but this returns true immediatly when it is not called too frequently.
export class Runbouncer {
    private bounceMillis: number;
    private prevMillis: number;
    private activeBounce = false;

    constructor(bounceMillis: number) {
        this.bounceMillis = bounceMillis;
        this.prevMillis = 0;
    }
    private nowMillis() {
        return (new Date()).getTime();
    }

    private isExpired(nowMillis: number) {
        return nowMillis > this.prevMillis + this.bounceMillis;
    }

    async bounce(): Promise<boolean> {
        if(this.activeBounce) { // If currently waiting we return false immediatly for this invocation
            return false;
        }
        const nowMillis = this.nowMillis();
        if(this.prevMillis === 0) { // First run always returns true immediatly
            this.prevMillis = nowMillis;
            return true;
        } else if(this.isExpired(nowMillis)) { // If expired return true immediatly
            this.prevMillis = nowMillis;
            return true;
        } else { // Less that bounceMillis since last return of true, wait to return next true.
            this.activeBounce = true;
            const delayMillis = this.bounceMillis - (nowMillis - this.prevMillis);
            const bouncePromise = new Promise<boolean>((resolve) => {
                setTimeout(() => {
                    this.prevMillis = this.nowMillis();
                    this.activeBounce = false;
                    resolve(true);
                }, delayMillis);
            });
            return bouncePromise;
        }

    }

    // Reset bouncer so that following call to bounce returns true immediatly
    reset() {
        this.prevMillis = 0;
    }
}

export class Debouncer {
    private cancellor: Function | null = null;
    private millis: number;

    constructor(waitMillis: number) {
        this.millis = waitMillis;
    }

    async debounce(): Promise<boolean> {
        if(this.cancellor != null) {
            this.cancellor();
        }
        const p = new Promise<boolean>((resolve) => {
            const cancelId = setTimeout(() => {
                this.cancellor = null;
                resolve(true);
            }, this.millis);
            this.cancellor = () => {
                clearTimeout(cancelId);
                resolve(false);
            }
        });
        return p;
    }
}

export function nowPath() {
    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

export function redirect(to: string) {
    window.location.assign(to);
}

// Return true if there is a non empty cookie with name "webuserSession"
export function hasWebuserSession() {
    return document.cookie.split(';').filter((item) => item.includes('isLoggedIn=1')).length > 0;
}

export const queryParams = (data: Dictionary<string | (number | null) | (string | null)[]>) => {
    let params: string[] = [];
    for(let key in data) {
        if(data.hasOwnProperty(key)) {
            const val = (data[key] as any) || null;
            if(val != null) {
                if(Array.isArray(val)) {
                    for(let v in val) {
                        params.push(encodeURIComponent(key)+"="+encodeURIComponent(v));
                    }
                } else {
                    const str = typeof val == "string" ? val : val.toString();
                    params.push(encodeURIComponent(key) + "=" + encodeURIComponent(str));
                }
            }
        }
    }
    if(params.length > 0) {
        return "?"+params.join("&");
    }
    return "";
};

export const isIos = () => {
    const ua = window.navigator.userAgent;
    return !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
};

export const isIosSafari = () => {
    const iOS = isIos();
    if(!iOS) {
        return false;
    }
    const ua = window.navigator.userAgent;
    const webkit = !!ua.match(/WebKit/i);
    return iOS && webkit && !ua.match(/CriOS/i);
};

export const isIosWebview = () => {
    if(isIos()) {
        // If safari is not in UA string, we're in the webview
        const ua = window.navigator.userAgent;
        return !ua.match(/Safari/i);
    }
    return false;
};

export const hasIosAppInterface = () => {
    let tmp = window["webkit"];
    tmp = tmp && tmp["messageHandlers"];
    tmp = tmp && tmp["connection"];
    tmp = tmp && tmp["postMessage"];
    return !!tmp;
};

export const isAndroid = () => {
    return window.navigator.userAgent.match(/\sAndroid\s/i);
};

export const setMeta = (name: string, content: string) => {
    const existing = document.querySelector(`meta[name="${name}"]`);
    if(existing != null) {
        existing.setAttribute("content", content);
    } else {
        const newMeta = document.createElement("meta");
        newMeta.name = name;
        newMeta.content = content;
        if(document.head) document.head.appendChild(newMeta);
    }
};

export const emailValidator = (email: string): boolean => /\S+@\S+\.\S+/.test(email);

export const postnumberValidator = (postnum: string): boolean => /^[0-9]{4}$/.test(postnum);

const intlNumFormNOK = new Intl.NumberFormat('no-NB', { style: 'currency', currency: 'NOK' });

export const numFormNOK = (num: number): string  => intlNumFormNOK.format(num);

export const isProdServer = window.location.host === "anleggsterminal.no";
